import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useAlerts } from '../../contexts/AlertContext';
import OrganizationTeamSelector from '../shared/OrganizationTeamSelector';
import Button from '../shared/Button';
import ResetButton from '../shared/ResetButton';
import SearchableDropdown from '../shared/SearchableDropdown';
import API from '../../API/API';
import log from '../../utils/logger';
import { Organization, Team } from '../../types/shared';

interface PlayerUnitSwapForm {
  organizationId: string;
  teamId: number | string | undefined;
  helmetBarcode: string;
  newPlayerUnitSerial: string;
  newPlayerUnitID: number;
  confirmSwap: boolean;
  confirmUnassignment?: boolean;
}

interface PlayerUnit {
  playerUnitSerialString: string;
  playerId?: string;
  playerName?: string;
  helmetId?: string;
  helmetSerial?: string;
  playerUnitID?: number;
  playersEquipmentID?: number;
}

export interface Helmet {
  helmetID: number;
  originalMaterialNo: string;
  helmetHatSize: string;
  features: string[];
  shell: string;
  shellColor: string;
  colorCode: string;
  paintCode1: string;
  paintCode1Description: string;
  paintCode2: string;
  paintCode2Description: string;
  paintDesignCode: string;
  paintDesignDescription: string;
  helmetType: string;
  helmetColor: string;
  rfidBarcode?: string;
  helmetRFIDLabel?: string;
  playerName?: string;
  firstName?: string;
  lastName?: string;
  jerseyNumber?: string;
  playerUnitSerialString?: string;
  playersEquipmentID?: number;
  playerUnitID?: number;
  playerID?: number;  // Added to capture player ID from API response
  positionID?: number;
  positionNameShort?: string;
}

interface HelmetInfo {
  playerUnitSerialString: string;
  sourceOrgID: number;
  sourceOrgName: string;
  jerseyNumber?: number;
  currentPlayer?: {
    playerName: string;
    playerNumber: string;
  };
  helmetID: number;
  helmetRFIDLabel?: string;
  rfidBarcode?: string;
  originalMaterialNo?: string;
  helmetHatSize: string;
  features: string[];
  shell: string;
  shellColor: string;
  colorCode: string;
  paintCode1: string;
  paintCode1Description: string;
  paintCode2: string;
  paintCode2Description: string;
  paintDesignCode: string;
  paintDesignDescription: string;
  helmetType: string;
  helmetColor: string;
}

const PlayerUnitSwapTool: React.FC = () => {
  const { addAlert } = useAlerts();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcessingSwap, setIsProcessingSwap] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [SourceTeamHelmets, setSourceTeamHelmets] = useState<Helmet[]>([]);
  const [unassignedPlayerUnits, setUnassignedPlayerUnits] = useState<PlayerUnit[]>([]);
  const [selectedHelmet, setSelectedHelmet] = useState<Helmet | null>(null);
  const [helmetInfo, setHelmetInfo] = useState<HelmetInfo | null>(null);
  const [newPlayerUnit, setNewPlayerUnit] = useState<PlayerUnit | null>(null);
  const [playerUnitConflict, setPlayerUnitConflict] = useState<{
    conflictHelmet: Helmet;
    requiresConfirmation: boolean;
  } | null>(null);
  const [hasConfirmedUnassignment, setHasConfirmedUnassignment] = useState<boolean>(false);
  const [isLoadingHelmets, setIsLoadingHelmets] = useState<boolean>(false);
  const [playerUnitValidationError, setPlayerUnitValidationError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid }
  } = useForm<PlayerUnitSwapForm>({
    mode: 'onChange', // Changed from 'onBlur' to make validation more responsive
    defaultValues: {
      organizationId: '',
      teamId: '',
      helmetBarcode: '',
      newPlayerUnitSerial: '',
      confirmSwap: false,
      confirmUnassignment: false
    }
  });

  const watchedValues = watch(['organizationId', 'teamId', 'helmetBarcode', 'newPlayerUnitSerial', 'confirmSwap', 'confirmUnassignment']);
  const [organizationId, teamId, helmetBarcode, newPlayerUnitSerial, confirmSwap, confirmUnassignment] = watchedValues;

  // Load organizations on component mount
  useEffect(() => {
    loadOrganizations();
  }, []);

  // Load teams when organization is selected
  useEffect(() => {
    if (organizationId) {
      // Clear team selection immediately when org changes
      setValue('teamId', '');
      loadTeams(organizationId);
    } else {
      setTeams([]);
      setSourceTeamHelmets([]);
      setValue('teamId', '');
    }
  }, [organizationId, setValue]);

  // Load helmet info when helmet is selected
  useEffect(() => {
    if (helmetBarcode && organizationId && SourceTeamHelmets.length > 0) {
      const selectedHelmetData = SourceTeamHelmets.find((helmet, index) => {
        const hID = helmet.rfidBarcode;
        return hID === helmetBarcode;
      });
      
      setSelectedHelmet(selectedHelmetData || null);
      
      if (selectedHelmetData) {
        // Create helmet info from the selected helmet data
        const newHelmetInfo: HelmetInfo = {
          playerUnitSerialString: selectedHelmetData.playerUnitSerialString || '',
          sourceOrgID: parseInt(organizationId),
          sourceOrgName: organizations.find(org => org.orgID.toString() === organizationId)?.orgName || '',
          jerseyNumber: selectedHelmetData.jerseyNumber ? Number(selectedHelmetData.jerseyNumber) : undefined,
          currentPlayer: selectedHelmetData.playerName ? {
            playerName: selectedHelmetData.playerName,
            playerNumber: selectedHelmetData.jerseyNumber || ''
          } : undefined,
          helmetRFIDLabel: selectedHelmetData.helmetRFIDLabel,
          rfidBarcode: selectedHelmetData.rfidBarcode,
          helmetID: selectedHelmetData.helmetID, // Added helmetID to HelmetInfo
          originalMaterialNo: selectedHelmetData.originalMaterialNo,
          helmetHatSize: selectedHelmetData.helmetHatSize,
          features: selectedHelmetData.features,
          shell: selectedHelmetData.shell,
          shellColor: selectedHelmetData.shellColor,
          colorCode: selectedHelmetData.colorCode,
          paintCode1: selectedHelmetData.paintCode1,
          paintCode1Description: selectedHelmetData.paintCode1Description,
          paintCode2: selectedHelmetData.paintCode2,
          paintCode2Description: selectedHelmetData.paintCode2Description,
          paintDesignCode: selectedHelmetData.paintDesignCode,
          paintDesignDescription: selectedHelmetData.paintDesignDescription,
          helmetType: selectedHelmetData.helmetType,
          helmetColor: selectedHelmetData.helmetColor
        };
        setHelmetInfo(newHelmetInfo);
        setCurrentStep(3);
      }
    } else {
      setSelectedHelmet(null);
      setHelmetInfo(null);
    }
  }, [helmetBarcode, organizationId, SourceTeamHelmets, organizations]);

  // Validate new player unit serial when entered
  useEffect(() => {
    if (newPlayerUnitSerial && newPlayerUnitSerial.length >= 9 && teamId) {
      validateNewPlayerUnit(newPlayerUnitSerial);
    }
  }, [newPlayerUnitSerial, teamId]);

  const loadOrganizations = async () => {
    try {
      setIsLoading(true);
      const response = await API.GetOrgs();
      
      // Follow the pattern from MovePlayerHelmetTool
      if (response && response.success && Array.isArray(response.data)) {
        setOrganizations(response.data as Organization[]);
      } else {
        setOrganizations([]);
        if (!response.success) {
          throw new Error(response.message || 'Failed to load organizations');
        }
      }
    } catch (error) {
      log.error('Failed to load organizations:', { error });
      addAlert('error', 'Failed to load organizations');
      setOrganizations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeams = async (orgId: string) => {
    try {
      setIsLoading(true);
      const response = await API.GetOrgTeams(orgId);
      
      // Follow similar pattern for teams
      if (response && response.success && Array.isArray(response.data)) {
        setTeams(response.data as Team[]);
      } else {
        setTeams([]);
        if (!response.success) {
          throw new Error(response.message || 'Failed to load teams');
        }
      }
    } catch (error) {
      log.error('Failed to load teams:', { error });
      addAlert('error', 'Failed to load teams for the selected organization');
      setTeams([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamHelmets = async (teamId: number) => {
    try {
      setIsLoadingHelmets(true);
      const response = await API.GetHelmets(teamId);
      
      if (response.success && response.data) {
        // Copy all fields from API response items
        const helmets: Helmet[] = response.data.map((item: any) => ({
          ...item,
          // Ensure essential fields have fallback values
          rfidBarcode: item.rfidBarcode || '',
          helmetRFIDLabel: item.helmetRFIDLabel || '',
          playerName: item.playerName || '',
          firstName: item.firstName || '',
          lastName: item.lastName || '',
          jerseyNumber: item.jerseyNumber || '',
          playerUnitSerialString: item.playerUnitSerialString || '',
        }));
        setSourceTeamHelmets(helmets);
      } else {
        setSourceTeamHelmets([]);
      }
    } catch (error) {
      log.error('Failed to load organization helmets:', { error });
      addAlert('error', 'Failed to load helmets for the selected organization');
      setSourceTeamHelmets([]);
    } finally {
      setIsLoadingHelmets(false);
    }
  };

  const loadUnassignedPlayerUnits = async () => {
    if (!teamId || typeof teamId !== 'number') return;
    
    try {
      setIsLoading(true);
      const response = await API.GetUnassignedPlayerUnits(teamId);
      
      // Handle different possible response structures
      let unassignedPlayerUnits: PlayerUnit[] = [];
      if (response) {
        if (Array.isArray(response)) {
          // Map the response to ensure proper field names
          unassignedPlayerUnits = response.map((item: any) => ({
            playerUnitSerialString: item.playerUnitSerialString || '',
            playerId: item.playerId || item.playerID || undefined,
            playerName: item.playerName || undefined,
            helmetId: item.helmetId || item.helmetID || undefined,
            helmetSerial: item.helmetSerial || undefined,
            playerUnitID: item.playerUnitID || item.id || undefined,
            playersEquipmentID: item.playersEquipmentID || undefined
          }));
        } else if (response.success && Array.isArray(response.data)) {
          // Map the response data to ensure proper field names
          unassignedPlayerUnits = response.data.map((item: any) => ({
            playerUnitSerialString: item.playerUnitSerialString || item.serialNumber || item.serial || '',
            // Keep original serialNumber if present for compatibility
            serialNumber: item.serialNumber || item.playerUnitSerialString || item.serial || '',
            playerId: item.playerId || item.playerID || undefined,
            playerName: item.playerName || undefined,
            helmetId: item.helmetId || item.helmetID || undefined,
            helmetSerial: item.helmetSerial || undefined,
            playerUnitID: item.playerUnitID || item.id || undefined,
            playersEquipmentID: item.playersEquipmentID || undefined
          }));
        } else if (response.rows && Array.isArray(response.rows)) {
          // Map the response rows to ensure proper field names
          unassignedPlayerUnits = response.rows.map((item: any) => ({
            playerUnitSerialString: item.playerUnitSerialString || item.serialNumber || item.serial || '',
            // Keep original serialNumber if present for compatibility
            serialNumber: item.serialNumber || item.playerUnitSerialString || item.serial || '',
            playerId: item.playerId || item.playerID || undefined,
            playerName: item.playerName || undefined,
            helmetId: item.helmetId || item.helmetID || undefined,
            helmetSerial: item.helmetSerial || undefined,
            playerUnitID: item.playerUnitID || item.id || undefined,
            playersEquipmentID: item.playersEquipmentID || undefined
          }));
        }
      }
      
      setUnassignedPlayerUnits(unassignedPlayerUnits);
    } catch (error) {
      log.error('Failed to load unassigned player units:', { error });
      addAlert('error', 'Failed to load player units for the selected team');
      setUnassignedPlayerUnits([]); // Ensure it's always an array
    } finally {
      setIsLoading(false);
    }
  };

  // TODO: We need additional API endpoints for full validation per spec:
  // 1. API.GetTeamPlayerUnits(teamId) - Get all player units assigned to team (assigned + unassigned)
  // 2. API.GetPlayerUnitDetails(serialNumber) - Get detailed info about a specific player unit
  // 3. API.ValidatePlayerUnitForSwap(teamId, serialNumber) - Validate if unit can be used for swapping
  // These would enable proper validation that the player unit:
  // - Is assigned to the selected team
  // - Is currently assigned to a player (but not a helmet)
  // - Is available for swapping

  const validateOldPlayerUnit = async (serial: string) => {
    // TODO: This function is no longer used - helmet selection provides player unit serial
    // Keeping for compatibility during refactor
  };

  const validatePlayerUnitSerialFormat = (playerUnitSerialString: string): boolean => {
    if (!playerUnitSerialString || typeof playerUnitSerialString !== 'string') return false;
    
    // Must be all numeric characters
    if (!/^\d+$/.test(playerUnitSerialString)) return false;
    
    const length = playerUnitSerialString.length;
    
    if (length === 10) {
      // Must start with 2-digit year between 22 and 40
      const year = parseInt(playerUnitSerialString.substring(0, 2));
      if (year < 22 || year > 40) return false;
      
      // Next 3 digits must represent day of year (001-365)
      const dayOfYear = parseInt(playerUnitSerialString.substring(2, 5));
      if (dayOfYear < 1 || dayOfYear > 365) return false;
      
      // Last 5 digits are unique identifier (any 5 digits)
      return true;
    }
    
    return false;
  };

  const validateNewPlayerUnit = useCallback(async (playerUnitSerialString: string) => {
    if (!playerUnitSerialString) {
      setNewPlayerUnit(null);
      setPlayerUnitConflict(null);
      setHasConfirmedUnassignment(false);
      setPlayerUnitValidationError(null);
      return;
    }

    // Check serial format first
    if (!validatePlayerUnitSerialFormat(playerUnitSerialString)) {
      setNewPlayerUnit(null);
      setPlayerUnitConflict(null);
      setHasConfirmedUnassignment(false);
      setPlayerUnitValidationError('Invalid player unit serial format number. Serial number must be 10 digits with year 22-40, day 001-365, and 5-digit ID.');
      return;
    }

    // Check if it's the same as current player unit
    if (helmetInfo && playerUnitSerialString === helmetInfo.playerUnitSerialString) {
      setNewPlayerUnit(null);
      setPlayerUnitConflict(null);
      setHasConfirmedUnassignment(false);
      setPlayerUnitValidationError('New player unit must be different from the current one');
      return;
    }

    // Step 1: Look for the player unit in the team helmeet data first (GetHelmets response includes ALL assigned team player units)
    const teamPlayerUnit = SourceTeamHelmets.find(helmet => 
      helmet.playerUnitSerialString === playerUnitSerialString
    );

    if (teamPlayerUnit) {
      // Player unit found in team data - check playersEquipmentID status FIRST
      
      // First check if it's the same helmet (redundant check but for safety)
      if (teamPlayerUnit.rfidBarcode === selectedHelmet?.rfidBarcode) {
        setNewPlayerUnit(null);
        setPlayerUnitConflict(null);
        setHasConfirmedUnassignment(false);
        setPlayerUnitValidationError('New player unit must be different from the current one');
        return;
      }
      
      // Check playersEquipmentID status BEFORE checking helmet assignment
      const hasPlayersEquipmentID = teamPlayerUnit.playersEquipmentID != null && 
                                     teamPlayerUnit.playersEquipmentID !== 0;
      
      if (hasPlayersEquipmentID) {
        // Player unit is assigned to a player - needs EndPlayersEquipment call
        // Check if it's also in a helmet for conflict handling
        if (teamPlayerUnit.rfidBarcode) {
          // Player unit is assigned to player AND in a helmet - requires confirmation
          setPlayerUnitConflict({
            conflictHelmet: teamPlayerUnit,
            requiresConfirmation: true
          });
          setNewPlayerUnit(null);
          setHasConfirmedUnassignment(false);
          setValue('confirmUnassignment', false);
          
          const conflictHelmetDisplay = teamPlayerUnit.helmetRFIDLabel || teamPlayerUnit.rfidBarcode || 'Unknown Helmet';
          const conflictPlayerDisplay = teamPlayerUnit.playerName ? 
            `${teamPlayerUnit.playerName} (#${teamPlayerUnit.jerseyNumber})` : 
            'Assigned Player';
          
          addAlert('warning', `Player unit ${playerUnitSerialString} is assigned to a player and in helmet ${conflictHelmetDisplay} (${conflictPlayerDisplay}). Please confirm you want to unassign it first.`);
          return;
        } else {
          // Player unit is assigned to player but NOT in a helmet - can proceed
          setNewPlayerUnit({
            playerUnitSerialString: playerUnitSerialString,
            playerId: 'unknown-player',
            playerName: teamPlayerUnit.playerName || 'Player (assigned to team)',
            helmetId: undefined,
            helmetSerial: undefined,
            playerUnitID: teamPlayerUnit.playerUnitID,
            playersEquipmentID: teamPlayerUnit.playersEquipmentID
          });
          setPlayerUnitConflict(null);
          setHasConfirmedUnassignment(false);
          setPlayerUnitValidationError(null);
          addAlert('info', `Player unit ${playerUnitSerialString} is assigned to a player and will be reassigned to ${teamPlayerUnit.playerName} during the swap`);
          return;
        }
      } else {
        // Player unit is NOT assigned to a player (playersEquipmentID is null/0)
        // Check if it's in a helmet
        if (teamPlayerUnit.rfidBarcode) {
          // Player unit is in a helmet but not assigned to player - conflict handling
          setPlayerUnitConflict({
            conflictHelmet: teamPlayerUnit,
            requiresConfirmation: true
          });
          setNewPlayerUnit(null);
          setHasConfirmedUnassignment(false);
          setValue('confirmUnassignment', false);
          
          const conflictHelmetDisplay = teamPlayerUnit.helmetRFIDLabel || teamPlayerUnit.rfidBarcode || 'Unknown Helmet';
          
          addAlert('warning', `Player unit ${playerUnitSerialString} is currently in helmet ${conflictHelmetDisplay} but not assigned to a player. Please confirm you want to reassign it.`);
          return;
        } else {
          // Player unit is not assigned to player and not in helmet - available
          setNewPlayerUnit({
            playerUnitSerialString: playerUnitSerialString,
            playerId: undefined,
            playerName: 'Not assigned to player',
            helmetId: undefined,
            helmetSerial: undefined,
            playerUnitID: teamPlayerUnit.playerUnitID
          });
          setPlayerUnitConflict(null);
          setHasConfirmedUnassignment(false);
          setPlayerUnitValidationError(null);
          addAlert('success', `Player unit ${playerUnitSerialString} is available for assignment`);
          return;
        }
      }
    }

    // Step 2: Fall back to checking unassigned player units list
    const unassignedList = Array.isArray(unassignedPlayerUnits) ? unassignedPlayerUnits : [];
    const isUnassigned = unassignedList.find(u => u.playerUnitSerialString === playerUnitSerialString);
    
    if (isUnassigned) {
      // Player unit is unassigned - OK to use
      setNewPlayerUnit({
        playerUnitSerialString: playerUnitSerialString,
        playerId: undefined,
        playerName: 'Unassigned',
        helmetId: undefined,
        helmetSerial: undefined,
        playerUnitID: isUnassigned.playerUnitID
      });
      setPlayerUnitConflict(null);
      setHasConfirmedUnassignment(false);
      setPlayerUnitValidationError(null);
      addAlert('success', `Player unit ${playerUnitSerialString} is available for assignment`);
      return;
    }

    // If we get here, the player unit was not found in any expected location
    setPlayerUnitValidationError(`Player unit ${playerUnitSerialString} not found. Please ensure it belongs to this team.`);
    setNewPlayerUnit(null);
  }, [SourceTeamHelmets, unassignedPlayerUnits, helmetInfo, selectedHelmet, addAlert, setValue]);

  const handleConfirmUnassignment = async () => {
    if (playerUnitConflict && newPlayerUnitSerial) {
      // Use the conflict helmet data directly - no need for additional API call
      const conflictHelmet = playerUnitConflict.conflictHelmet;
      
      setHasConfirmedUnassignment(true);
      setNewPlayerUnit({
        playerUnitSerialString: newPlayerUnitSerial,
        playerId: conflictHelmet.playerName ? 'conflicted-player' : undefined,
        playerName: conflictHelmet.playerName || 'Player (will be unassigned)',
        helmetId: conflictHelmet.rfidBarcode,
        helmetSerial: conflictHelmet.helmetRFIDLabel,
        playerUnitID: conflictHelmet.playerUnitID,
        playersEquipmentID: conflictHelmet.playersEquipmentID
      });
      addAlert('success', `Confirmed: Player unit ${newPlayerUnitSerial} will be unassigned from current helmet and reassigned`);
    }
  };

  const handleOrgChange = (orgId: string | number) => {
    setValue('organizationId', orgId.toString());
    setValue('teamId', '' as any); // Reset team selection when org changes - use empty string for select elements  
    setValue('helmetBarcode', '');
    setValue('newPlayerUnitSerial', '');
    setValue('confirmSwap', false);
    setValue('confirmUnassignment', false);
    setHelmetInfo(null);
    setNewPlayerUnit(null);
    setPlayerUnitConflict(null);
    setHasConfirmedUnassignment(false);
    
    // Update step progression
    if (orgId) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  };

  const handleTeamChange = (teamId: number) => {
    setValue('teamId', teamId);
    setValue('helmetBarcode', '');
    setValue('newPlayerUnitSerial', '');
    setValue('confirmSwap', false);
    setValue('confirmUnassignment', false);
    setHelmetInfo(null);
    setNewPlayerUnit(null);
    setPlayerUnitConflict(null);
    setHasConfirmedUnassignment(false);
    
    if (teamId) {
      // Stay at step 2 for helmet selection
      setCurrentStep(2);
    }
  };

  // Auto-select team if only one team available, but only after teams are loaded for current org
  useEffect(() => {
    if (organizationId && teams.length === 1 && (!teamId || teamId === '')) {
      // Only auto-select if no team is currently selected
      setValue('teamId', teams[0].teamID);
      // Don't auto-advance step - user needs to select helmet
    } else if (organizationId && teams.length > 1 && (typeof teamId !== 'number' || !teamId)) {
      // For multi-team orgs, ensure teamId is cleared to show placeholder
      setValue('teamId', '');
    }
  }, [organizationId, teams, setValue, teamId]);

  // Load helmets when team is selected
  useEffect(() => {
    if (teamId && typeof teamId === 'number' && teamId > 0) {
      loadTeamHelmets(teamId);
      loadUnassignedPlayerUnits(); // Also load unassigned player units when team is selected
    } else {
      // Clear helmets when no team is selected
      setSourceTeamHelmets([]);
      // Also clear any selected helmet
      setSelectedHelmet(null);
      setHelmetInfo(null);
      setValue('helmetBarcode', '');
    }
  }, [teamId, setValue]);

  // Create helmet options for dropdown
  const helmetOptions = useMemo(() => 
    SourceTeamHelmets
      .sort((a, b) => {
        // Sort by last name, then first name
        const lastNameA = (a.lastName || '').toLowerCase();
        const lastNameB = (b.lastName || '').toLowerCase();
        
        if (lastNameA === lastNameB) {
          const firstNameA = (a.firstName || '').toLowerCase();
          const firstNameB = (b.firstName || '').toLowerCase();
          return firstNameA.localeCompare(firstNameB);
        }
        
        // Put entries without last names at the end
        if (!lastNameA && lastNameB) return 1;
        if (lastNameA && !lastNameB) return -1;
        if (!lastNameA && !lastNameB) return 0;
        
        return lastNameA.localeCompare(lastNameB);
      })
      .map((helmet, index) => {
        const helmetBarcode = helmet.rfidBarcode || `helmet-${index}`;
        const rfidLabel = helmet.helmetRFIDLabel || 'N/A';
        const rfidBarcode = helmet.rfidBarcode || 'N/A';
        const lastName = helmet.lastName || '';
        const firstName = helmet.firstName || '';
        const jerseyNumber = helmet.jerseyNumber || '';
        const playerUnitSerial = helmet.playerUnitSerialString || '';
        
        // Format: rfid label [rfid barcode] lastName, firstName (#jerseyNumber) [playerUnitSerialString]
        let displayLabel = `${rfidLabel} [${rfidBarcode}]`;
        
        if (lastName || firstName) {
          const playerName = `${lastName}${firstName ? ', ' + firstName : ''}`;
          const jerseyDisplay = jerseyNumber ? ` (#${jerseyNumber})` : '';
          displayLabel += ` ${playerName}${jerseyDisplay}`;
        } else {
          displayLabel += ' Unassigned';
        }
        
        if (playerUnitSerial) {
          displayLabel += ` [${playerUnitSerial}]`;
        }
        
        return { helmetBarcode, displayLabel };
      }), [SourceTeamHelmets]
  );

  const handlePlayerUnitChange = () => {
    if (helmetInfo && newPlayerUnit) {
      setCurrentStep(3);
    } else if (helmetInfo) {
      setCurrentStep(3); // Move to step 3 for new player unit selection
    } else {
      setCurrentStep(2);
    }
  };

  /**
   * Performs the player unit swap operation.
   * @param data The form data for the player unit swap.
   * @returns A promise that resolves when the swap is complete.
   */
  const performSwap = async (data: PlayerUnitSwapForm) => {
    if (!helmetInfo || !newPlayerUnit || !selectedHelmet) {
      addAlert('error', 'Please select both a helmet and new player unit');
      return;
    }

    try {
      setIsProcessingSwap(true);
      
      // Step 1: Handle the old player's equipment assignment
      // If the helmet's existing player unit is assigned to a player, end that assignment
      if (selectedHelmet.playersEquipmentID) {
        await API.EndPlayersEquipment(selectedHelmet.playersEquipmentID);
      } else {
        log.warn('No playersEquipmentID found for selected helmet, skipping EndPlayersEquipment call');
      }
            
      // Step 2: Update helmet with new player unit
      // Use helmet ID from the selected helmet info and the playerUnitID from the new player unit
      if (selectedHelmet?.rfidBarcode && newPlayerUnit.playerUnitID) {
        await API.PutHelmet(selectedHelmet, newPlayerUnit.playerUnitSerialString);
      }

      // Step 3: Assign the new player unit to the target player
      // Use PutPlayersTeamsEquipment which handles both ending old assignments and creating new ones
      if (selectedHelmet.playerID && newPlayerUnit.playerUnitID) {
        await API.PutPlayersTeamsEquipment({
          playerID: selectedHelmet.playerID,
          teamID: data.teamId,
          playerUnitID: newPlayerUnit.playerUnitID,
          playerUnitSerialString: newPlayerUnit.playerUnitSerialString,
          lastName: selectedHelmet.lastName || '',
          firstName: selectedHelmet.firstName || '',
          positionID: selectedHelmet.positionID || 2, // Default to position ID 2 (RB) if not present
        });
      }

      addAlert('success', 'Player unit swap completed successfully!');
      handleReset();
      
    } catch (error) {
      log.error('Failed to perform player unit swap:', { error });
      addAlert('error', 'Failed to complete player unit swap. Please try again.');
    } finally {
      setIsProcessingSwap(false);
    }
  };

  const handleReset = () => {
    reset();
    setCurrentStep(1);
    setHelmetInfo(null);
    setSelectedHelmet(null);
    setNewPlayerUnit(null);
    setUnassignedPlayerUnits([]);
    setSourceTeamHelmets([]);
    setPlayerUnitConflict(null);
    setHasConfirmedUnassignment(false);
  };

  // Update step when helmet info or player units change
  useEffect(() => {
    handlePlayerUnitChange();
  }, [helmetInfo, newPlayerUnit]);

  return (
    <div className="w-full max-w-7xl mx-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6 bg-neutral-50 min-h-screen">
      {/* Riddell Header */}
      <div className="bg-riddell-red text-white rounded-t-lg shadow-lg mb-3 sm:mb-4 lg:mb-6 w-full">
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-2 sm:space-y-0 w-full">
            <div className="bg-white p-1.5 sm:p-2 rounded flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-riddell-red rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm sm:text-lg">R</span>
              </div>
            </div>
            <div className="text-center sm:text-left flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-heading font-black tracking-tight">PLAYER UNIT SWAP</h1>
              <p className="text-riddell-red-100 text-sm sm:text-base lg:text-lg font-medium">Equipment Management System</p>
            </div>
            {/* Reset Button */}
            <ResetButton
              isVisible={!!(organizationId || teamId || helmetInfo || newPlayerUnit)}
              onReset={handleReset}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-xl border border-neutral-200 w-full overflow-visible">
        <div className="p-3 sm:p-4 lg:p-6 w-full">
          <form onSubmit={handleSubmit(performSwap)} className="space-y-4 sm:space-y-6 lg:space-y-8 w-full">
            {/* Step 1: Source Organization & Team Selection */}
            <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="source-selection-heading">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">1</div>
                <h2 id="source-selection-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT SOURCE ORGANIZATION</h2>
              </div>

              <div className="w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Source Organization */}
                  <div>
                    <label htmlFor="organizationId" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                      Source Organization
                    </label>
                    <SearchableDropdown
                      {...register('organizationId', { required: 'Source organization is required' })}
                      id="organizationId"
                      name="organizationId"
                      value={watch('organizationId') || ''}
                      onChange={(value) => {
                        setValue('organizationId', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                        handleOrgChange(value)
                      }}
                      options={organizations
                        .filter(org => org.orgName && org.orgName.trim() !== '')
                        .map(org => ({
                          value: org.orgID.toString(),
                          label: org.orgName
                        }))}
                      placeholder="Search source organization..."
                      disabled={isLoading && organizations.length === 0}
                      error={errors.organizationId?.message}
                      required={true}
                      aria-busy={isLoading}
                    />
                    {isLoading && organizations.length === 0 && (
                      <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                        <span className="text-sm font-medium">Loading organizations...</span>
                      </div>
                    )}
                  </div>

                  {/* Source Team Dropdown - only show if multiple teams */}
                  {organizationId && teams.length > 1 && (
                    <div>
                      <label htmlFor="teamId" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Source Team
                      </label>
                      <SearchableDropdown
                        {...register('teamId', { required: 'Source team is required when multiple teams exist' })}
                        id="teamId"
                        name="teamId"
                        value={typeof teamId === 'number' ? teamId.toString() : ''}
                        onChange={(value) => {
                          setValue('teamId', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                          handleTeamChange(Number(value))
                        }}
                        options={teams.map(team => ({
                          value: team.teamID.toString(),
                          label: team.teamName
                        }))}
                        placeholder="Select source team..."
                        error={errors.teamId?.message}
                        required={true}
                      />
                    </div>
                  )}

                  {/* Source Team (Auto-selected) - only show if exactly one team */}
                  {organizationId && teams.length === 1 && (
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Source Team (Auto-selected)
                      </label>
                      <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-gray-100 border-2 border-gray-300 rounded-md">
                        {teams[0]?.teamName || 'Loading...'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Team Information Display */}
                {organizationId && teamId && typeof teamId === 'number' && organizations.length > 0 && teams.length > 0 && (
                  <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-md">
                    <div className="flex items-center space-x-2 text-success">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium">
                        Selected: {organizations.find(org => org.orgID.toString() === organizationId)?.orgName} - {teams.find(team => team.teamID === teamId)?.teamName}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Helmet Selection */}
            {organizationId && teamId && typeof teamId === 'number' && teamId > 0 && teams.length > 0 && teams.some(team => team.teamID === teamId) && (
              <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="helmet-selection-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">2</div>
                  <h2 id="helmet-selection-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT HELMET</h2>
                </div>
                <div className="w-full">
                  <label htmlFor="helmetID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                    <span className="block sm:inline">Helmet from</span> <span className="block sm:inline font-medium">{organizations.find(org => org.orgID.toString() === organizationId)?.orgName}</span>
                  </label>
                  <SearchableDropdown
                    {...register('helmetBarcode', { required: 'Helmet selection is required' })}
                    id="helmetID"
                    name="helmetBarcode"
                    value={watch('helmetBarcode') || ''}
                    onChange={(value) => {
                      setValue('helmetBarcode', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                    }}
                    options={helmetOptions.map((option) => ({
                      value: option.helmetBarcode,
                      label: option.displayLabel
                    }))}
                    placeholder={isLoadingHelmets ? 'Loading helmets...' : helmetOptions.length === 0 ? 'No helmets available for this team' : 'Search helmet...'}
                    disabled={isLoadingHelmets}
                    error={errors.helmetBarcode?.message}
                    required={true}
                    aria-busy={isLoadingHelmets}
                  />
                  {isLoadingHelmets && (
                    <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                      <span className="text-sm font-medium">Loading helmets...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Helmet Information Display */}
            {helmetInfo && (
              <div className="bg-success/10 border-l-4 border-success p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="helmet-info-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-success text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">3</div>
                  <h3 id="helmet-info-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">HELMET INFORMATION</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3 text-xs sm:text-sm w-full">
                  <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                    <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Source Org</span>
                    <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">{helmetInfo.sourceOrgName}</span>
                  </div>
                  <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                    <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Label</span>
                    <span className="text-sm sm:text-base font-bold text-riddell-red">{helmetInfo.helmetRFIDLabel || 'N/A'}</span>
                  </div>
                  <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                    <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Barcode</span>
                    <span className="text-sm sm:text-base font-medium text-riddell-black break-all leading-tight">{helmetInfo.rfidBarcode || 'N/A'}</span>
                  </div>
                  <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                    <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Current Player Unit</span>
                    <span className="text-sm sm:text-base font-medium text-riddell-black break-all leading-tight">{helmetInfo.playerUnitSerialString}</span>
                  </div>
                  {selectedHelmet && (selectedHelmet.playerName || selectedHelmet.firstName || selectedHelmet.lastName) && (
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Current Player</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">
                        {selectedHelmet.playerName || `${selectedHelmet.firstName || ''} ${selectedHelmet.lastName || ''}`.trim()} 
                        {selectedHelmet.jerseyNumber && ` (#${selectedHelmet.jerseyNumber})`}
                      </span>
                    </div>
                  )}
                  {helmetInfo.currentPlayer && (
                    <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Player</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">
                        {helmetInfo.currentPlayer.playerName} (#{helmetInfo.currentPlayer.playerNumber})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: New Player Unit Selection */}
            {helmetInfo && (
              <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="new-unit-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">4</div>
                  <h2 id="new-unit-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT NEW PLAYER UNIT</h2>
                </div>
                <div className="w-full">
                  <label htmlFor="newPlayerUnitSerial" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                    New Player Unit Serial Number
                  </label>
                  <input
                    type="text"
                    {...register('newPlayerUnitSerial', {
                      required: 'New player unit serial number is required',
                      validate: {
                        format: (value) => {
                          if (value.length < 9) return true; // Don't validate until more than 9 characters
                          return validatePlayerUnitSerialFormat(value) || 'Invalid player unit serial format number. Serial number must be 10 digits with year 22-40, day 001-365, and 5-digit ID.';
                        },
                        different: (value) => {
                          if (value.length < 9) return true; // Don't validate until more than 9 characters
                          return value !== helmetInfo?.playerUnitSerialString || 'New player unit must be different from the current one';
                        },
                        available: (value) => {
                          if (value.length < 9) return true; // Don't validate until more than 9 characters
                          // Don't show validation error if async validation is still processing or if there's no conflict
                          if (playerUnitConflict && !hasConfirmedUnassignment) return 'Please confirm unassignment of the conflicting player unit';
                          return true;
                        }
                      }
                    })}
                    id="newPlayerUnitSerial"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150"
                    placeholder="Enter new player unit serial (assigned to another player)"
                  />
                  {(errors.newPlayerUnitSerial || playerUnitValidationError) && (
                    <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.newPlayerUnitSerial?.message || playerUnitValidationError}
                    </p>
                  )}
                  
                  {newPlayerUnit && (
                    <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-md">
                      <div className="flex items-center space-x-2 text-success mb-2">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium">Valid Player Unit for Swap</span>
                      </div>
                      <p className="text-sm text-riddell-black">Serial: {newPlayerUnit.playerUnitSerialString}</p>
                      {newPlayerUnit.playerName && (
                        <p className="text-sm text-riddell-black">Currently assigned to: {newPlayerUnit.playerName}</p>
                      )}
                      <p className="text-sm text-riddell-black">Status: Available for swap</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Player Unit Conflict Confirmation */}
            {playerUnitConflict && playerUnitConflict.requiresConfirmation && (
              <div className="bg-warning/10 border-l-4 border-warning p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="conflict-warning-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-warning text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">!</div>
                  <h2 id="conflict-warning-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">PLAYER UNIT ALREADY ASSIGNED</h2>
                </div>
                
                <div className="bg-white border border-warning/30 rounded-md p-4 mb-4">
                  <h3 className="text-lg font-medium mb-3 text-riddell-black">Conflict Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm w-full">
                    <div className="bg-gray-50 p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Label</span>
                      <span className="text-sm sm:text-base font-bold text-riddell-red">{playerUnitConflict.conflictHelmet.helmetRFIDLabel || 'N/A'}</span>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Barcode</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-all leading-tight">{playerUnitConflict.conflictHelmet.rfidBarcode || 'N/A'}</span>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded-md shadow-sm w-full">
                      <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Player Unit</span>
                      <span className="text-sm sm:text-base font-medium text-riddell-black break-all leading-tight">{newPlayerUnitSerial}</span>
                    </div>
                    {(playerUnitConflict.conflictHelmet.playerName || playerUnitConflict.conflictHelmet.firstName || playerUnitConflict.conflictHelmet.lastName) && (
                      <div className="bg-gray-50 p-2 sm:p-3 rounded-md shadow-sm w-full">
                        <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Player</span>
                        <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">
                          {playerUnitConflict.conflictHelmet.playerName || `${playerUnitConflict.conflictHelmet.firstName || ''} ${playerUnitConflict.conflictHelmet.lastName || ''}`.trim()} 
                          {playerUnitConflict.conflictHelmet.jerseyNumber && ` (#${playerUnitConflict.conflictHelmet.jerseyNumber})`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-error/10 border border-error/20 rounded-md p-4 mb-4">
                  <h4 className="font-medium text-error mb-2">Action Required</h4>
                  <p className="text-sm text-riddell-black mb-3">
                    To proceed with this swap, the player unit will be:
                  </p>
                  <ol className="list-decimal list-inside text-sm text-riddell-black space-y-1 ml-4">
                    <li>Unassigned from the current helmet ({playerUnitConflict.conflictHelmet.helmetRFIDLabel || playerUnitConflict.conflictHelmet.rfidBarcode})</li>
                    <li>Unassigned from the current player ({playerUnitConflict.conflictHelmet.playerName || `${playerUnitConflict.conflictHelmet.firstName || ''} ${playerUnitConflict.conflictHelmet.lastName || ''}`.trim() || 'Unknown'})</li>
                    <li>Assigned to the selected helmet ({selectedHelmet?.helmetRFIDLabel || selectedHelmet?.rfidBarcode})</li>
                  </ol>
                </div>

                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    {...register('confirmUnassignment', {
                      required: playerUnitConflict.requiresConfirmation ? 'Please confirm the unassignment before proceeding' : false
                    })}
                    className="h-4 w-4 text-riddell-red focus:ring-riddell-red border-gray-300 rounded"
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleConfirmUnassignment();
                      } else {
                        setHasConfirmedUnassignment(false);
                        setNewPlayerUnit(null);
                      }
                    }}
                  />
                  <label className="ml-2 block text-sm text-riddell-black">
                    I understand and confirm that I want to unassign this player unit from its current assignment before reassigning it
                  </label>
                </div>
                {errors.confirmUnassignment && (
                  <p className="text-sm text-error font-medium mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.confirmUnassignment.message}
                  </p>
                )}
              </div>
            )}

            {/* Step 5: Confirmation */}
            {helmetInfo && newPlayerUnit && (!playerUnitConflict || hasConfirmedUnassignment) && (
              <div className="bg-riddell-red/10 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="confirm-swap-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">{playerUnitConflict ? '6' : '5'}</div>
                  <h2 id="confirm-swap-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">CONFIRM PLAYER UNIT SWAP</h2>
                </div>
                
                <div className="bg-warning/10 border border-warning/20 rounded-md p-4 mb-4">
                  <h3 className="text-lg font-medium mb-3 text-riddell-black">Swap Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-error mb-1">Removing:</p>
                      <p className="text-sm text-riddell-black">Player Unit: {helmetInfo.playerUnitSerialString}</p>
                      {helmetInfo.currentPlayer && (
                        <p className="text-sm text-riddell-black">From Player: {helmetInfo.currentPlayer.playerName}</p>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-success mb-1">Adding:</p>
                      <p className="text-sm text-riddell-black">Player Unit: {newPlayerUnit.playerUnitSerialString}</p>
                      <p className="text-sm text-riddell-black">To Same Helmet</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    {...register('confirmSwap', {
                      required: 'Please confirm the swap operation'
                    })}
                    className="h-4 w-4 text-riddell-red focus:ring-riddell-red border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-riddell-black">
                    I confirm that I want to remove player unit {helmetInfo.playerUnitSerialString} and swap player unit {newPlayerUnit.playerUnitSerialString} into helmet with {selectedHelmet?.rfidBarcode}
                  </label>
                </div>
                {errors.confirmSwap && (
                  <p className="text-sm text-error font-medium mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.confirmSwap.message}
                  </p>
                )}

                {/* Submit Button */}
                <div className="w-full">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!confirmSwap || isProcessingSwap || !isValid}
                    className="w-full py-3 sm:py-4 text-base sm:text-lg font-bold uppercase tracking-wide bg-riddell-red text-white border-2 border-riddell-red rounded-md hover:bg-riddell-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    {isProcessingSwap ? (
                      <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                        <span>SWAPPING...</span>
                      </div>
                    ) : (
                      'SWAP PLAYER UNITS'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {isProcessingSwap && (
              <div className="mt-4">
                <div className="text-center">Processing swap...</div>
              </div>
            )}
      </form>
        </div>
      </div>
    </div>
  );
};

export default PlayerUnitSwapTool;
